import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  TrendingDown, DollarSign, Leaf, Plane, Ship, Award, Download,
  Sparkles, ShieldCheck, Target, Brain, CheckCircle2, AlertTriangle,
  Calendar, Save, Pencil
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, BarChart, Bar, RadialBarChart, RadialBar, PolarAngleAxis, ReferenceLine,
} from "recharts";
import { toast } from "@/hooks/use-toast";

// ── Editable stats (persisted) ──────────────────────────────
export type CVCStats = {
  monthlyPax: number;
  heliDay: number;
  vesselDay: number;
  paxPerHeliDay: number;
  paxPerVesselDay: number;
  tonnesAvoidedYTD: number;
  carbonPrice: number;
  msaScore: number;
  uptimeSla: number;
  onTimeArrival: number;
  co2PerPax: number;
};

const DEFAULTS: CVCStats = {
  monthlyPax: 5400,
  heliDay: 18000,
  vesselDay: 14500,
  paxPerHeliDay: 18,
  paxPerVesselDay: 64,
  tonnesAvoidedYTD: 4280,
  carbonPrice: 85,
  msaScore: 96,
  uptimeSla: 99.4,
  onTimeArrival: 98.1,
  co2PerPax: 14.2,
};

const STORAGE_KEY = "voff-cvc-stats-v1";

const useCVCStats = () => {
  const [stats, setStats] = useState<CVCStats>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });
  const save = (next: CVCStats) => {
    setStats(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };
  return { stats, save };
};

// ── Counter ──
const useCounter = (target: number, duration = 1100) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0; const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const fmtUSD = (n: number, frac = 0) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: frac });
const fmtNum = (n: number, frac = 0) => n.toLocaleString("en-US", { maximumFractionDigits: frac });

// ── Stats Editor (intranet only) ──────────────────────────────
const StatsEditor = ({ stats, onSave }: { stats: CVCStats; onSave: (s: CVCStats) => void }) => {
  const [draft, setDraft] = useState<CVCStats>(stats);
  const [open, setOpen] = useState(false);
  useEffect(() => setDraft(stats), [stats]);

  const fields: { k: keyof CVCStats; label: string; suffix?: string }[] = [
    { k: "monthlyPax", label: "Monthly PAX moves" },
    { k: "heliDay", label: "Heli day-rate", suffix: "$" },
    { k: "vesselDay", label: "AIRCAT day-rate", suffix: "$" },
    { k: "paxPerHeliDay", label: "PAX per heli/day" },
    { k: "paxPerVesselDay", label: "PAX per vessel/day" },
    { k: "tonnesAvoidedYTD", label: "tCO₂e avoided YTD" },
    { k: "carbonPrice", label: "Carbon price ($/t)" },
    { k: "msaScore", label: "MSA composite (0-100)" },
    { k: "uptimeSla", label: "Vessel uptime %" },
    { k: "onTimeArrival", label: "On-time arrival %" },
    { k: "co2PerPax", label: "CO₂/PAX (kg)" },
  ];

  return (
    <div className="mb-6 rounded-xl border border-teal/30 bg-teal/5 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pencil className="w-4 h-4 text-teal" />
          <p className="text-sm font-bold text-foreground">Commercial Inputs</p>
          <Badge variant="outline" className="text-[10px]">Drives client-facing dashboards</Badge>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)}>
          {open ? "Collapse" : "Edit Stats"}
        </Button>
      </div>
      {open && (
        <>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
            {fields.map(f => (
              <div key={f.k}>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                <Input
                  type="number"
                  value={draft[f.k]}
                  onChange={e => setDraft({ ...draft, [f.k]: parseFloat(e.target.value) || 0 })}
                  className="h-8 text-sm"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => setDraft(DEFAULTS)}>Reset</Button>
            <Button size="sm" onClick={() => { onSave(draft); toast({ title: "Stats saved", description: "Client portal dashboards updated." }); }}>
              <Save className="w-3 h-3 mr-1" /> Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Value Twin ──
const ValueTwin = ({ s }: { s: CVCStats }) => {
  const [vesselMix, setVesselMix] = useState([72]);
  const mix = vesselMix[0] / 100;

  const heliPax = s.monthlyPax * (1 - mix);
  const vesselPax = s.monthlyPax * mix;
  const heliCost = (heliPax / s.paxPerHeliDay) * s.heliDay;
  const vesselCost = (vesselPax / s.paxPerVesselDay) * s.vesselDay;
  const totalCost = heliCost + vesselCost;
  const baselineAllHeli = (s.monthlyPax / s.paxPerHeliDay) * s.heliDay;
  const savings = baselineAllHeli - totalCost;
  const annualSavings = savings * 12;
  const costPerPax = totalCost / s.monthlyPax;
  const baselinePerPax = baselineAllHeli / s.monthlyPax;

  const animSavings = useCounter(annualSavings);
  const animPerPax = useCounter(costPerPax);

  const trend = useMemo(() => Array.from({ length: 12 }).map((_, i) => {
    const m = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"][i];
    const baseline = baselinePerPax;
    const actual = baseline - (i * (baseline - costPerPax) / 12);
    return { m, baseline: Math.round(baseline), actual: Math.round(actual) };
  }), [baselinePerPax, costPerPax]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-[hsl(215,40%,15%)] via-[hsl(211,55%,20%)] to-[hsl(195,70%,25%)] p-7 text-white relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[hsl(160,80%,45%)]/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[hsl(160,90%,60%)]" />
            <p className="text-[10px] font-bold tracking-[3px] uppercase text-white/70">Live P&L Twin</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-2">Commercial Value Twin</h2>
          <p className="text-sm text-white/70 max-w-2xl mb-6">
            Live mirror of crew-logistics P&amp;L. Move the slider to model AIRCAT-vs-helicopter mix and watch annualized savings recompute.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-5">
              <p className="text-[10px] uppercase tracking-[2px] text-white/50 mb-2">Annualized Savings</p>
              <p className="font-display text-3xl font-bold text-[hsl(160,90%,60%)]">{fmtUSD(animSavings)}</p>
              <div className="flex items-center gap-1 mt-2 text-[11px] text-white/60">
                <TrendingDown className="w-3 h-3 text-[hsl(160,90%,60%)]" /> vs all-heli baseline
              </div>
            </div>
            <div className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-5">
              <p className="text-[10px] uppercase tracking-[2px] text-white/50 mb-2">Cost per PAX-Move</p>
              <p className="font-display text-3xl font-bold">{fmtUSD(animPerPax)}</p>
              <p className="text-[11px] text-white/60 mt-2">Baseline: {fmtUSD(baselinePerPax)}</p>
            </div>
            <div className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-5">
              <p className="text-[10px] uppercase tracking-[2px] text-white/50 mb-2">Productive Hrs On-Platform/mo</p>
              <p className="font-display text-3xl font-bold">{fmtNum(s.monthlyPax * 9.4)}</p>
              <p className="text-[11px] text-[hsl(160,90%,60%)] mt-2">+18% vs heli-only</p>
            </div>
          </div>
          <div className="rounded-xl bg-black/20 border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold tracking-[2px] uppercase text-white/70">AIRCAT Share of PAX Moves</p>
              <p className="font-display text-xl font-bold text-[hsl(160,90%,60%)]">{vesselMix[0]}%</p>
            </div>
            <Slider value={vesselMix} onValueChange={setVesselMix} min={0} max={100} step={1} className="my-3" />
            <div className="grid grid-cols-3 gap-3 mt-4 text-[11px] text-white/70">
              <div>Heli PAX/mo: <span className="text-white font-semibold">{fmtNum(heliPax)}</span></div>
              <div>Vessel PAX/mo: <span className="text-white font-semibold">{fmtNum(vesselPax)}</span></div>
              <div>Monthly Spend: <span className="text-white font-semibold">{fmtUSD(totalCost)}</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-bold text-sm mb-4">Cost-per-PAX Trend (12 mo)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={trend}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(160,90%,45%)" stopOpacity={0.4} />
                <stop offset="100%" stopColor="hsl(160,90%,45%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,15%,90%)" />
            <XAxis dataKey="m" fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            <Area type="monotone" dataKey="baseline" stroke="hsl(0,70%,55%)" strokeDasharray="4 4" fill="none" name="Heli Baseline" />
            <Area type="monotone" dataKey="actual" stroke="hsl(160,90%,45%)" fill="url(#g1)" name="Velocity" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ── Scorecard ──
const ScorecardKPI = ({ label, value, target, unit, status }: any) => {
  const pct = Math.min(100, (value / target) * 100);
  const color = status === "green" ? "hsl(160,80%,42%)" : status === "amber" ? "hsl(38,90%,50%)" : "hsl(0,75%,55%)";
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[2px] text-muted-foreground">{label}</p>
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      </div>
      <p className="font-display text-2xl font-bold">{value}{unit}</p>
      <p className="text-[10px] text-muted-foreground mt-1">MSA target: {target}{unit}</p>
      <div className="h-1.5 bg-muted rounded-full mt-3 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const ContractScorecard = ({ s }: { s: CVCStats }) => {
  const kpis = [
    { label: "Vessel Uptime SLA", value: s.uptimeSla, target: 98, unit: "%", status: "green" },
    { label: "On-Time Crew Arrival", value: s.onTimeArrival, target: 95, unit: "%", status: "green" },
    { label: "CO₂ / PAX-Move", value: s.co2PerPax, target: 18, unit: "kg", status: "green" },
    { label: "Safety LTI Rate", value: 0, target: 0.2, unit: "", status: "green" },
    { label: "Invoice Accuracy", value: 99.7, target: 99, unit: "%", status: "green" },
    { label: "Liquidated Damages Exposure", value: 0, target: 0, unit: "K", status: "green" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 rounded-xl border border-[hsl(160,80%,45%)]/30 bg-gradient-to-br from-[hsl(160,80%,45%)]/8 to-transparent p-6 text-center">
          <p className="text-[10px] uppercase tracking-[3px] text-muted-foreground mb-3">MSA Composite Score</p>
          <ResponsiveContainer width="100%" height={170}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ v: s.msaScore, fill: "hsl(160,80%,42%)" }]} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background dataKey="v" cornerRadius={20} />
            </RadialBarChart>
          </ResponsiveContainer>
          <p className="font-display text-4xl font-bold text-[hsl(160,80%,38%)] -mt-24">{s.msaScore}</p>
          <p className="text-[10px] text-muted-foreground mt-20">Top-decile vs benchmark</p>
          <Badge className="mt-3 bg-[hsl(160,80%,42%)]/15 text-[hsl(160,80%,32%)] border-0">EXCEEDING SLA</Badge>
        </div>
        <div className="md:col-span-2 grid sm:grid-cols-2 gap-3">
          {kpis.map(k => <ScorecardKPI key={k.label} {...k} />)}
        </div>
      </div>
      <div className="rounded-2xl border border-border p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Award className="w-10 h-10 text-primary" />
          <div>
            <h3 className="font-bold text-sm">Quarterly Business Review — Q1 2026</h3>
            <p className="text-xs text-muted-foreground">Auto-generated, board-ready PDF.</p>
          </div>
        </div>
        <Button onClick={() => toast({ title: "QBR PDF generating…" })}>
          <Download className="w-4 h-4 mr-2" /> Generate QBR
        </Button>
      </div>
    </div>
  );
};

// ── Predictive ──
const PredictivePlanner = ({ s }: { s: CVCStats }) => {
  const days = useMemo(() => Array.from({ length: 14 }).map((_, i) => {
    const date = new Date(); date.setDate(date.getDate() + i);
    const baseline = (s.monthlyPax / 30) + Math.sin(i / 2) * 40;
    return {
      d: date.toLocaleDateString("en-US", { weekday: "short", day: "numeric" }),
      forecast: Math.round(baseline + Math.random() * 30),
      weatherOk: i !== 4 && i !== 9,
    };
  }), [s.monthlyPax]);
  const [sel, setSel] = useState(0);
  const day = days[sel];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-[hsl(265,80%,55%)]" />
            <h3 className="font-bold text-sm">14-Day PAX Demand Forecast</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">Confidence: 94%</Badge>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={days}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,15%,90%)" />
            <XAxis dataKey="d" fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            <ReferenceLine y={256} stroke="hsl(0,70%,55%)" strokeDasharray="4 4" />
            <Bar dataKey="forecast" radius={[6, 6, 0, 0]} fill="hsl(211,100%,40%)" onClick={(_, i) => setSel(i)} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-primary" />
            <p className="text-[10px] uppercase tracking-[2px] text-muted-foreground">Selected Day</p>
          </div>
          <p className="font-display text-xl font-bold">{day.d}</p>
          <p className="text-xs text-muted-foreground mt-1">{day.forecast} predicted PAX</p>
          <div className="mt-3 flex items-center gap-2 text-[11px]">
            {day.weatherOk
              ? <><CheckCircle2 className="w-3 h-3 text-[hsl(160,80%,42%)]" /><span className="text-[hsl(160,80%,32%)]">Weather: GO</span></>
              : <><AlertTriangle className="w-3 h-3 text-[hsl(38,90%,50%)]" /><span className="text-[hsl(38,90%,40%)]">Sea state 4-5</span></>}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-[10px] uppercase tracking-[2px] text-muted-foreground mb-3">Recommended Allocation</p>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between"><span className="flex items-center gap-1.5"><Ship className="w-3 h-3 text-primary" />Velocity Pioneer</span><span className="font-semibold">112 PAX</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-1.5"><Ship className="w-3 h-3 text-primary" />Velocity Endeavour</span><span className="font-semibold">96 PAX</span></div>
            <div className="flex justify-between text-muted-foreground"><span className="flex items-center gap-1.5"><Plane className="w-3 h-3" />Heli backup</span><span>0</span></div>
          </div>
          <Button size="sm" variant="outline" className="w-full mt-4 text-xs">
            <Target className="w-3 h-3 mr-1" /> Lock Allocation
          </Button>
        </div>
        <div className="rounded-xl border border-border bg-gradient-to-br from-[hsl(160,80%,45%)]/8 to-transparent p-5">
          <p className="text-[10px] uppercase tracking-[2px] text-muted-foreground mb-3">Cost-to-Serve Optimization</p>
          <p className="font-display text-2xl font-bold text-[hsl(160,80%,38%)]">−{fmtUSD(42800)}</p>
          <p className="text-xs text-muted-foreground mt-1">vs unoptimized day plan</p>
          <div className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[hsl(160,80%,42%)]" /> Heli stand-down avoided</div>
            <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[hsl(160,80%,42%)]" /> 1.4 t Jet A-1 saved</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Carbon-to-Cash ──
const CarbonToCash = ({ s }: { s: CVCStats }) => {
  const [carbonPrice, setCarbonPrice] = useState([s.carbonPrice]);
  useEffect(() => setCarbonPrice([s.carbonPrice]), [s.carbonPrice]);
  const ytdValue = s.tonnesAvoidedYTD * carbonPrice[0];
  const animValue = useCounter(ytdValue, 900);
  const data = useMemo(() => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep"].map((m, i) => ({
    m, avoided: Math.round((s.tonnesAvoidedYTD / 9) * (0.6 + i * 0.08)),
  })), [s.tonnesAvoidedYTD]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-[hsl(160,60%,15%)] via-[hsl(170,55%,22%)] to-[hsl(180,60%,28%)] p-7 text-white relative overflow-hidden">
        <div className="relative grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Leaf className="w-4 h-4 text-[hsl(140,90%,65%)]" />
              <p className="text-[10px] font-bold tracking-[3px] uppercase text-white/70">Scope 3 · Logistics</p>
            </div>
            <h2 className="font-display text-3xl font-bold mb-2">Carbon-to-Cash</h2>
            <p className="text-sm text-white/70 mb-4">Translate avoided Scope 3 emissions into dollars at your internal carbon price.</p>
            <div className="rounded-xl bg-black/20 border border-white/10 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[2px] text-white/60">Internal Carbon Price</p>
                <p className="font-display text-lg font-bold">{fmtUSD(carbonPrice[0])}/tCO₂e</p>
              </div>
              <Slider value={carbonPrice} onValueChange={setCarbonPrice} min={20} max={200} step={5} />
            </div>
          </div>
          <div className="space-y-3">
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <p className="text-[10px] uppercase tracking-[2px] text-white/60 mb-1">YTD Carbon-Equivalent Value</p>
              <p className="font-display text-4xl font-bold text-[hsl(140,90%,65%)]">{fmtUSD(animValue)}</p>
              <p className="text-[11px] text-white/60 mt-1">{fmtNum(s.tonnesAvoidedYTD)} tCO₂e avoided</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] uppercase tracking-[2px] text-white/60">CBAM Exposure</p>
                <p className="font-display text-lg font-bold mt-1">{fmtUSD(ytdValue * 0.18)}</p>
              </div>
              <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] uppercase tracking-[2px] text-white/60">SLB Coupon Saved</p>
                <p className="font-display text-lg font-bold mt-1">12 bps</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-bold text-sm mb-4">Avoided Emissions — Monthly (tCO₂e)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,15%,90%)" />
            <XAxis dataKey="m" fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            <Line type="monotone" dataKey="avoided" stroke="hsl(160,80%,42%)" strokeWidth={3} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex justify-end mt-4">
          <Button variant="outline" size="sm" onClick={() => toast({ title: "SLB pack export started" })}>
            <Download className="w-3 h-3 mr-1" /> Export SLB Pack
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main body ──
export const CommercialValueCenterBody = ({ editable = false }: { editable?: boolean }) => {
  const { stats, save } = useCVCStats();
  return (
    <>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-primary/60 mb-1">For the Commercial Office</p>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          Commercial Value Center
          <Badge className="bg-gradient-to-r from-[hsl(160,80%,42%)] to-[hsl(180,80%,45%)] text-white border-0 text-[10px]">LIVE</Badge>
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          P&amp;L mirror, MSA scorecard, predictive demand, and carbon-to-cash — one Monday-morning view.
        </p>
      </div>

      {editable && <StatsEditor stats={stats} onSave={save} />}

      <Tabs defaultValue="twin" className="w-full">
        <TabsList className="bg-card border border-border mb-6 p-1 h-auto flex-wrap">
          <TabsTrigger value="twin" className="text-xs"><DollarSign className="w-3.5 h-3.5 mr-1.5" />Value Twin</TabsTrigger>
          <TabsTrigger value="score" className="text-xs"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Scorecard</TabsTrigger>
          <TabsTrigger value="plan" className="text-xs"><Brain className="w-3.5 h-3.5 mr-1.5" />Predictive Planner</TabsTrigger>
          <TabsTrigger value="carbon" className="text-xs"><Leaf className="w-3.5 h-3.5 mr-1.5" />Carbon-to-Cash</TabsTrigger>
        </TabsList>
        <TabsContent value="twin"><ValueTwin s={stats} /></TabsContent>
        <TabsContent value="score"><ContractScorecard s={stats} /></TabsContent>
        <TabsContent value="plan"><PredictivePlanner s={stats} /></TabsContent>
        <TabsContent value="carbon"><CarbonToCash s={stats} /></TabsContent>
      </Tabs>
    </>
  );
};

export default CommercialValueCenterBody;
